import React from 'react';
import Icon from '../../../components/appIcon';
import Image from '../../../components/appImage';
import Button from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';

const EmployeeMobileCards = ({
  employees = [],
  selectedEmployees = [],
  onSelectEmployee,
  onEditEmployee,
  onViewEmployee,
  onUploadPhoto,
  onToggleStatus
}) => {
  // Status Badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success/10 text-success border-success/20', label: 'Active' },
      inactive: { color: 'bg-muted text-muted-foreground border-border', label: 'Inactive' },
      suspended: { color: 'bg-error/10 text-error border-error/20', label: 'Suspended' }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            status === 'active'
              ? 'bg-success'
              : status === 'suspended'
              ? 'bg-error'
              : 'bg-muted-foreground'
          }`}
        />
        {config.label}
      </span>
    );
  };

  // Employment Type Badge
  const getEmploymentTypeBadge = (type) => {
    const typeConfig = {
      permanent: { color: 'bg-primary/10 text-primary border-primary/20', label: 'Permanent' },
      contract: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Contract' },
      intern: { color: 'bg-accent/10 text-accent border-accent/20', label: 'Intern' },
      freelance: { color: 'bg-secondary/10 text-secondary border-secondary/20', label: 'Freelance' }
    };

    const config = typeConfig[type] || typeConfig.contract;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  // Format Last Activity
  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'No activity';

    const now = new Date();
    const activity = new Date(timestamp);
    const diffInHours = Math.floor((now - activity) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return activity.toLocaleDateString();
  };

  // Empty State
  if (!employees.length) {
    return (
      <div className="text-center py-12">
        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No employees found</h3>
        <p className="text-muted-foreground mb-4">No employees match your current filters.</p>
        <Button variant="outline" onClick={() => window.location?.reload()}>
          <Icon name="RefreshCw" size={16} className="mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {employees.map((employee) => (
        <div
          key={employee?.id}
          className={`bg-card border border-border rounded-lg p-4 enterprise-shadow-card ${
            selectedEmployees.includes(employee?.id)
              ? 'ring-2 ring-primary/20 border-primary/30'
              : ''
          }`}
        >
          {/* Header with Checkbox and Actions */}
          <div className="flex items-start justify-between mb-3">
            <Checkbox
              checked={selectedEmployees.includes(employee?.id)}
              onChange={(e) => onSelectEmployee?.(employee?.id, e?.target?.checked)}
            />
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEditEmployee?.(employee)}
                className="h-8 w-8"
                aria-label="Edit Employee"
              >
                <Icon name="Edit" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onUploadPhoto?.(employee)}
                className="h-8 w-8"
                aria-label="Upload Photo"
              >
                <Icon name="Camera" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onToggleStatus?.(employee)}
                className={`h-8 w-8 ${
                  employee?.status === 'active'
                    ? 'text-error hover:text-error'
                    : 'text-success hover:text-success'
                }`}
                aria-label={employee?.status === 'active' ? 'Deactivate Employee' : 'Activate Employee'}
              >
                <Icon name={employee?.status === 'active' ? 'UserX' : 'UserCheck'} size={16} />
              </Button>
            </div>
          </div>

          {/* Employee Info */}
          <div
            className="cursor-pointer"
            onClick={() => onViewEmployee?.(employee)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onViewEmployee?.(employee)}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="relative">
                <Image
                  src={employee?.profileImage || '/images/default-avatar.png'}
                  alt={employee?.name || 'Employee'}
                  className="w-12 h-12 rounded-full object-cover border border-border"
                />
                {employee?.status === 'active' && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success border-2 border-card rounded-full" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground truncate">{employee?.name}</h3>
                <p className="text-sm text-muted-foreground font-mono">{employee?.employeeId}</p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-xs text-muted-foreground">Department</p>
                <p className="text-sm text-foreground">{employee?.department || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Role</p>
                <p className="text-sm text-foreground">{employee?.role || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Location</p>
                <p className="text-sm text-foreground">{employee?.location || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Last Activity</p>
                <p className="text-sm text-foreground">{formatLastActivity(employee?.lastActivity)}</p>
              </div>
            </div>

            {/* Status and Type Badges */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusBadge(employee?.status)}
                {getEmploymentTypeBadge(employee?.employmentType)}
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EmployeeMobileCards;
